import { useState, useEffect } from 'react';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { useParams } from 'react-router-dom';
import {
  Award, CheckCircle, XCircle, Building2, Calendar, Trees,
  Weight, MapPin, Loader2, AlertTriangle, Shield
} from 'lucide-react';
import type { CertificateVerification } from '../../types/cultivar';
import cultivarService from '../../services/cultivar';
import PbrMark from '../../components/cultivar/PbrMark';
import PbrNotice from '../../components/cultivar/PbrNotice';
import { formatNumeric } from '@/utils/numeric';

function VerificationPage() {
  const timezone = useOrgTimezone();
  const { token } = useParams<{ token: string }>();
  const [verification, setVerification] = useState<CertificateVerification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      verifyCertificate(token);
    } else {
      setError('No verification token provided');
      setLoading(false);
    }
  }, [token]);

  const verifyCertificate = async (verificationToken: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await cultivarService.verifyCertificate(verificationToken);
      if (response.success && response.data) {
        setVerification(response.data);
      } else {
        setError('Certificate not found or invalid');
      }
    } catch (err) {
      console.error('Error verifying certificate:', err);
      setError('Failed to verify certificate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'N/A';
    return formatLocalDateInTz(date, timezone, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full">
          <Loader2 className="h-12 w-12 text-primary-600 animate-spin mx-auto mb-4" />
          <h1 className="text-xl font-bold text-neutral-900 mb-2">Verifying Certificate</h1>
          <p className="text-neutral-600">Please wait while we verify this certificate...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !verification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-error-50 to-error-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full">
          <div className="h-16 w-16 rounded-full bg-error-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-error-600" />
          </div>
          <h1 className="text-xl font-bold text-neutral-900 mb-2">Verification Failed</h1>
          <p className="text-neutral-600 mb-6">{error || 'Unable to verify this certificate.'}</p>
          <p className="text-sm text-neutral-500">
            If you believe this is an error, please contact the certificate issuer.
          </p>
        </div>
      </div>
    );
  }

  const isValid = verification.status === 'active' &&
    new Date(verification.valid_until) >= new Date();

  // Success - Valid Certificate
  if (isValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-success-50 to-success-100 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Success Banner */}
            <div className="bg-success-600 text-white p-6">
              <div className="flex items-center justify-center gap-3">
                <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckCircle className="h-7 w-7" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Valid Certificate</h1>
                  <p className="text-success-100">This certificate has been verified</p>
                </div>
              </div>
            </div>

            {/* Certificate Details */}
            <div className="p-6 sm:p-8">
              {/* Certificate Number */}
              <div className="text-center mb-6 pb-6 border-b border-neutral-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Award className="h-5 w-5 text-primary-600" />
                  <span className="text-sm text-neutral-500 uppercase tracking-wide">
                    Certificate Number
                  </span>
                </div>
                <p className="text-2xl font-bold text-neutral-900">
                  {verification.certificate_number}
                </p>
              </div>

              {/* Cultivar & License Info */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-success-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-success-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Cultivar</p>
                    <p className="font-semibold text-neutral-900">
                      {verification.cultivar_name}
                      {/* Public certificate — variety is PBR-protected by definition. */}
                      <PbrMark status="protected" />
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Licensed To</p>
                    <p className="font-semibold text-neutral-900">{verification.licensee_org_name}</p>
                    <p className="text-sm text-neutral-500">
                      {verification.license_tier} License
                      {verification.region && ` • ${verification.region}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-neutral-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">License Granted By</p>
                    <p className="font-semibold text-neutral-900">{verification.licensor_org_name}</p>
                  </div>
                </div>
              </div>

              {/* Declared Volumes */}
              {(verification.declared_trees || verification.declared_production_tonnes) && (
                <div className="mb-6 p-4 bg-neutral-50 rounded-xl">
                  <p className="text-sm font-medium text-neutral-700 mb-3">
                    Acknowledged Production Data
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {verification.declared_trees && (
                      <div className="flex items-center gap-3">
                        <Trees className="h-5 w-5 text-success-500" />
                        <div>
                          <p className="font-bold text-neutral-900">
                            {formatNumeric(verification.declared_trees)}
                          </p>
                          <p className="text-xs text-neutral-500">Trees Planted</p>
                        </div>
                      </div>
                    )}
                    {verification.declared_hectares && (
                      <div className="flex items-center gap-3">
                        <MapPin className="h-5 w-5 text-primary-500" />
                        <div>
                          <p className="font-bold text-neutral-900">
                            {formatNumeric(verification.declared_hectares)}
                          </p>
                          <p className="text-xs text-neutral-500">Hectares</p>
                        </div>
                      </div>
                    )}
                    {verification.declared_production_tonnes && (
                      <div className="flex items-center gap-3">
                        <Weight className="h-5 w-5 text-warning-500" />
                        <div>
                          <p className="font-bold text-neutral-900">
                            {formatNumeric(verification.declared_production_tonnes)}t
                          </p>
                          <p className="text-xs text-neutral-500">Production</p>
                        </div>
                      </div>
                    )}
                    {verification.season_year && (
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-purple-500" />
                        <div>
                          <p className="font-bold text-neutral-900">
                            {verification.season_year}
                          </p>
                          <p className="text-xs text-neutral-500">Season</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PBR notice — IP Australia rules require symbol + warning together. */}
              <PbrNotice className="mb-6" />

              {/* Validity Dates */}
              <div className="flex items-center justify-between p-4 bg-success-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-success-600" />
                  <div>
                    <p className="text-xs text-success-600">Valid From</p>
                    <p className="font-medium text-success-900">{formatDate(verification.valid_from)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-success-600">Valid Until</p>
                  <p className="font-medium text-success-900">{formatDate(verification.valid_until)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-neutral-600 mt-6">
            Verified at {new Date().toLocaleString()} • Cultivar Entitlement System
          </p>
        </div>
      </div>
    );
  }

  // Invalid / Expired / Revoked Certificate
  return (
    <div className="min-h-screen bg-gradient-to-br from-error-50 to-error-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Error Banner */}
          <div className="bg-error-600 text-white p-6">
            <div className="flex items-center justify-center gap-3">
              <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                <XCircle className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">
                  {verification.status === 'revoked' ? 'Revoked Certificate' :
                   verification.status === 'expired' ? 'Expired Certificate' :
                   'Invalid Certificate'}
                </h1>
                <p className="text-error-100">This certificate is no longer valid</p>
              </div>
            </div>
          </div>

          {/* Certificate Details */}
          <div className="p-6 sm:p-8">
            <div className="text-center mb-6">
              <p className="text-lg font-bold text-neutral-900">
                {verification.certificate_number}
              </p>
              <p className="text-neutral-600">
                {verification.cultivar_name}
                <PbrMark status="protected" />
              </p>
            </div>

            {/* PBR notice — symbol + warning must travel together. */}
            <PbrNotice className="mb-6" />

            {/* Status Explanation */}
            <div className="p-4 bg-error-50 border border-error-200 rounded-xl mb-6">
              {verification.status === 'revoked' && (
                <div>
                  <p className="font-medium text-error-800">This certificate was revoked</p>
                  {verification.revoked_reason && (
                    <p className="text-sm text-error-700 mt-1">
                      Reason: {verification.revoked_reason}
                    </p>
                  )}
                  {verification.revoked_at && (
                    <p className="text-sm text-error-600 mt-1">
                      Revoked on: {formatDate(verification.revoked_at)}
                    </p>
                  )}
                </div>
              )}
              {verification.status === 'expired' && (
                <div>
                  <p className="font-medium text-error-800">This certificate has expired</p>
                  <p className="text-sm text-error-700 mt-1">
                    Expired on: {formatDate(verification.valid_until)}
                  </p>
                </div>
              )}
            </div>

            {/* Original Details */}
            <div className="space-y-3 text-sm text-neutral-600">
              <p><strong>Licensee:</strong> {verification.licensee_org_name}</p>
              <p><strong>Licensor:</strong> {verification.licensor_org_name}</p>
              <p><strong>License Type:</strong> {verification.license_tier}</p>
              {verification.region && <p><strong>Region:</strong> {verification.region}</p>}
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-neutral-600 mt-6">
          Please contact the issuing organisation for more information.
        </p>
      </div>
    </div>
  );
}

export default VerificationPage;
